/**
 * 認証状態を React コンポーネントから使うための hook。
 *
 * 責務:
 *   - `auth` 状態を購読して再レンダリングを起こす
 *   - `signInGoogle / signInApple / signInLINE / signOut` のラッパーを提供
 *   - サインイン呼び出しは結果オブジェクトを返し、UI が
 *     「キャンセル」と「失敗」を区別できるようにする
 *
 * Hub への連携（linkAccount / recoverAccount）は呼び出し側では扱わない。
 * ここでは `auth.ts` が返す AuthResult を state に反映するところまでが責務で、
 * T13-T15 で Hub の session をさらに要求するフローを追加するときにここから linkAccount を呼ぶ想定。
 */

import { useCallback, useEffect, useState } from 'react'
import type { AuthProvider } from '../config/auth'
import {
  AuthCancelledError,
  commitAuthResult,
  getAuthState,
  signInWithApple,
  signInWithGoogle,
  signInWithLINE,
  signOut as signOutInternal,
  subscribeAuthState,
  type AuthResult,
  type AuthState,
} from '../services/auth'
import { linkAccount, recoverAccount } from '../services/accountLink'
import { syncHubProfile } from '../services/hubProfile'

export type SignInOutcome =
  | { ok: true }
  | { ok: false; reason: 'cancelled' }
  | { ok: false; reason: 'needs_recovery_confirm'; provider: AuthProvider; idToken: string }
  | { ok: false; reason: 'error'; error: string }

export interface UseAuthResult {
  auth: AuthState
  isSignedIn: boolean
  lastError: string | null
  signInGoogle: () => Promise<SignInOutcome>
  signInApple: () => Promise<SignInOutcome>
  signInLINE: () => Promise<SignInOutcome>
  reauthenticate: (provider: AuthProvider) => Promise<SignInOutcome>
  confirmRecovery: (provider: AuthProvider, idToken: string) => Promise<SignInOutcome>
  signOut: () => Promise<void>
  clearError: () => void
}

export function useAuth(): UseAuthResult {
  const [auth, setAuth] = useState<AuthState>(() => getAuthState())
  const [lastError, setLastError] = useState<string | null>(null)

  useEffect(() => {
    const unsub = subscribeAuthState(setAuth)
    return () => {
      unsub()
    }
  }, [])

  const runSignIn = useCallback(
    async (fn: () => Promise<AuthResult>): Promise<SignInOutcome> => {
      setLastError(null)
      try {
        const result = await fn()
        if (result.provider === 'line') {
          await commitAuthResult(result)
          try {
            await syncHubProfile()
          } catch (profileErr) {
            console.warn('[useAuth] post-line-sign-in profile sync failed:', profileErr)
          }
          return { ok: true }
        }

        const linkResult = await linkAccount(result.provider, result.idToken)
        if (!linkResult.ok && linkResult.needsRecoveryConfirm) {
          return {
            ok: false,
            reason: 'needs_recovery_confirm',
            provider: result.provider,
            idToken: result.idToken,
          }
        }
        if (!linkResult.ok) {
          throw new Error(linkResult.message ?? '/auth/link failed')
        }
        await commitAuthResult(result)
        // 連携成功直後に Hub プロフィールを fresh 取得しておく。
        // App.tsx の subscribeAuthState リスナーでも syncHubProfile は走るが、
        // AuthLoginModal が isSignedIn を見て即座にモーダルを閉じるため、
        // ここで明示的に await することで「モーダルを閉じた瞬間に名前が
        // 切り替わっている」状態を保証する。失敗してもサインイン自体は
        // 成功扱い（リスナー側で再試行され、必要なら再認証 UI が出る）。
        try {
          await syncHubProfile()
        } catch (profileErr) {
          console.warn('[useAuth] post-link profile sync failed:', profileErr)
        }
        return { ok: true }
      } catch (err) {
        if (err instanceof AuthCancelledError) {
          return { ok: false, reason: 'cancelled' }
        }
        const message = err instanceof Error ? err.message : String(err)
        setLastError(message)
        console.warn('[useAuth] sign-in failed:', err)
        return { ok: false, reason: 'error', error: message }
      }
    },
    [],
  )

  const signInGoogle = useCallback(() => runSignIn(() => signInWithGoogle()), [runSignIn])
  const signInApple = useCallback(() => runSignIn(() => signInWithApple()), [runSignIn])
  const signInLINE = useCallback(() => runSignIn(() => signInWithLINE()), [runSignIn])

  const reauthenticate = useCallback(
    async (provider: AuthProvider): Promise<SignInOutcome> => {
      setLastError(null)
      try {
        const result =
          provider === 'google'
            ? await signInWithGoogle()
            : provider === 'apple'
              ? await signInWithApple()
              : await signInWithLINE()
        if (result.provider === 'google' || result.provider === 'apple') {
          const recovered = await recoverAccount(result.provider, result.idToken)
          if (!recovered.ok) {
            throw new Error(recovered.message ?? 'recoverAccount failed')
          }
        }
        await commitAuthResult(result)
        try {
          await syncHubProfile()
        } catch (profileErr) {
          console.warn('[useAuth] post-reauth profile sync failed:', profileErr)
        }
        return { ok: true }
      } catch (err) {
        if (err instanceof AuthCancelledError) {
          return { ok: false, reason: 'cancelled' }
        }
        const message = err instanceof Error ? err.message : String(err)
        setLastError(message)
        console.warn('[useAuth] re-authentication failed:', err)
        return { ok: false, reason: 'error', error: message }
      }
    },
    [],
  )

  const confirmRecovery = useCallback(
    async (provider: AuthProvider, idToken: string): Promise<SignInOutcome> => {
      setLastError(null)
      try {
        if (provider === 'line') {
          throw new Error('LINE account recovery is handled by LIFF login')
        }
        const result = await recoverAccount(provider, idToken)
        if (!result.ok) {
          throw new Error(result.message ?? 'recoverAccount failed')
        }
        await commitAuthResult({ provider, idToken })
        // 復旧成功直後に Hub プロフィールを fresh 取得（runSignIn と同じ理由）。
        // 復旧で session token が新しいユーザーのものに切り替わるため、
        // /profiles/me はここで叩けば既存アカウント（例:「ぴよこ」）の
        // displayName を返す。
        try {
          await syncHubProfile()
        } catch (profileErr) {
          console.warn('[useAuth] post-recover profile sync failed:', profileErr)
        }
        return { ok: true }
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err)
        setLastError(message)
        console.warn('[useAuth] recover confirm failed:', err)
        return { ok: false, reason: 'error', error: message }
      }
    },
    [],
  )

  const doSignOut = useCallback(async () => {
    setLastError(null)
    try {
      await signOutInternal()
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      setLastError(message)
      console.warn('[useAuth] sign-out failed:', err)
    }
  }, [])

  const clearError = useCallback(() => setLastError(null), [])

  return {
    auth,
    isSignedIn: auth.status === 'signed_in',
    lastError,
    signInGoogle,
    signInApple,
    signInLINE,
    reauthenticate,
    confirmRecovery,
    signOut: doSignOut,
    clearError,
  }
}
